import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { createAsyncThunk, AsyncThunk } from '@reduxjs/toolkit';

import { ThunkExtra, RootState } from 'application/store';
import RootFrontendUtils from 'application/utils/RootFrontendUtils';

import {
  UserExtendPureType,
  AppInitializationQuery,
  AppInitializationDocument,
  AuthCurrentUserQuery,
  AuthCurrentUserDocument,
  CompanyContractKind,
} from 'domain/api/graphql/generated';
import { FetchPolicyKeys } from 'domain/api/graphql/types';
import { UserDataType } from 'domain/auth/types';

import CompanyUtils from 'application/utils/CompanyUtils';

import { ACCOUNT_FETCH_CONFIG } from './consts';
import { AccountDataPayload } from './types';

const checkApolloCacheHasData = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  companyId: string
): Promise<boolean> => {
  try {
    const cachedData = apolloClient.cache.readQuery<AppInitializationQuery>({
      query: AppInitializationDocument,
      variables: { companyId },
    });

    return !!cachedData?.companyBillingInfoQuery;
  } catch {
    return false;
  }
};

const processAccountData = (appData?: AppInitializationQuery): AccountDataPayload | null => {
  const user = appData?.currentUserPureQuery as UserExtendPureType;

  if (!user) return null;

  const isReadyToUseLK = RootFrontendUtils.isReadyToUseLK(user, user.mainCompany);
  const currentCompanyId = user.mainCompany?.id || null;
  const isBillingEnabled = user.mainCompany?.isBillingEnabled || false;
  const streamLibraryId = user.mainCompany?.streamLibrariesIds?.length
    ? String(user.mainCompany.streamLibrariesIds[0])
    : undefined;

  const isWithoutCompanies = user.companies === undefined || user.companies === null || user.companies.length === 0;

  const hasActiveContractMigration =
    !isWithoutCompanies &&
    !!CompanyUtils.getCompanyWithActiveContractMigration(user.companies, CompanyContractKind.Offerta);

  const userData: UserDataType = {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    smartLkAccess: user.smartLkAccess,
    registrationSource: user.registrationSource,
    mainCompany: user.mainCompany,
    pointsExists: user.pointsExists,
    companies: user.companies,
    phone: user.phone,
    isTest: user.isTest,
    personalAccountAccess: user.personalAccountAccess,
  };

  return {
    currentCompanyId,
    streamLibraryId,
    isBillingEnabled,
    isReadyToUseLK,
    isWithoutCompanies,
    hasActiveContractMigration,
    userData,
  };
};

const executeAccountQuery = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  companyId: string | undefined,
  _: (value: string) => any
): Promise<AccountDataPayload> => {
  try {
    if (!companyId) {
      throw new Error('Company ID is required for initialization query');
    }

    const { data, error } = await apolloClient.query<AppInitializationQuery>({
      query: AppInitializationDocument,
      fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
      variables: {
        companyId,
      },
    });

    if (error) {
      throw new Error(`GraphQL Error: ${error.message}`);
    }

    const billingInfo = data.companyBillingInfoQuery;

    if (billingInfo) {
      const newData = {
        ...data,
        companyBillingInfoQuery: {
          ...billingInfo,
        },
      };

      apolloClient.cache.writeQuery({
        query: AppInitializationDocument,
        variables: { companyId },
        data: newData,
      });
    }

    const processedData = processAccountData(data);

    if (!processedData) {
      throw new Error('Invalid user data structure');
    }

    return processedData;
  } catch (error: any) {
    if (error.networkError) {
      throw new Error(`Network error: ${error.networkError.message}`);
    }

    throw error;
  }
};

const executeCurrentUserQuery = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  _: (value: string) => any
): Promise<AccountDataPayload> => {
  try {
    const { data, error } = await apolloClient.query<AuthCurrentUserQuery>({
      query: AuthCurrentUserDocument,
      fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
    });

    if (error) {
      throw new Error(`GraphQL Error: ${error.message}`);
    }

    apolloClient.cache.writeQuery({
      query: AuthCurrentUserDocument,
      data,
    });

    const processedData = processAccountData(data);

    if (!processedData) {
      throw new Error('Invalid user data structure');
    }

    return processedData;
  } catch (error: any) {
    if (error.networkError) {
      throw new Error(`Network error: ${error.networkError.message}`);
    }

    throw error;
  }
};

const fetchAccountDataUntyped = createAsyncThunk(
  'account/fetchData',
  async (companyId: string, { extra, rejectWithValue, getState }) => {
    const { apolloClient } = extra as ThunkExtra;
    const state = getState() as RootState;
    const accountState = state.account;

    const targetCompanyId = companyId || accountState.currentCompanyId;

    try {
      return await executeAccountQuery(apolloClient, targetCompanyId, rejectWithValue);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const refreshAccountDataUntyped = createAsyncThunk(
  'account/refreshData',
  async (companyId: string, { dispatch, extra, rejectWithValue, getState }) => {
    const { apolloClient } = extra as ThunkExtra;
    const state = getState() as RootState;
    const accountState = state.account;

    const targetCompanyId = companyId || accountState.currentCompanyId;

    dispatch({ type: 'account/markDataAsStale' });

    try {
      return await executeAccountQuery(apolloClient, targetCompanyId, rejectWithValue);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const initializeAccountDataUntyped = createAsyncThunk(
  'account/initialize',
  async (companyId: string, { getState, extra, rejectWithValue }) => {
    const { apolloClient } = extra as ThunkExtra;
    const state = getState() as RootState;
    const accountState = state.account;

    const targetCompanyId = companyId || accountState.currentCompanyId;

    const hasApolloCacheData = await checkApolloCacheHasData(apolloClient, companyId);

    const shouldFetch =
      !accountState.userData?.id ||
      accountState.dataStale ||
      !targetCompanyId ||
      !hasApolloCacheData ||
      (accountState.lastFetchTime && Date.now() - accountState.lastFetchTime > ACCOUNT_FETCH_CONFIG.staleTime);

    if (!shouldFetch) {
      return null;
    }

    try {
      return await executeAccountQuery(apolloClient, targetCompanyId, rejectWithValue);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const fetchCurrentUserDataUntyped = createAsyncThunk(
  'account/fetchCurrentUserData',
  async (_, { extra, rejectWithValue }) => {
    const { apolloClient } = extra as ThunkExtra;

    try {
      return await executeCurrentUserQuery(apolloClient, rejectWithValue);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAccountData = fetchAccountDataUntyped as AsyncThunk<
  AccountDataPayload,
  string | undefined,
  {
    state: RootState;
    extra: ThunkExtra;
    rejectValue: string;
  }
>;

export const refreshAccountData = refreshAccountDataUntyped as AsyncThunk<
  AccountDataPayload,
  string | undefined,
  {
    state: RootState;
    extra: ThunkExtra;
    rejectValue: string;
  }
>;

export const initializeAccountData = initializeAccountDataUntyped as AsyncThunk<
  AccountDataPayload | null,
  string | undefined,
  {
    state: RootState;
    extra: ThunkExtra;
    rejectValue: string;
  }
>;

export const fetchCurrentUserData = fetchCurrentUserDataUntyped as AsyncThunk<
  AccountDataPayload,
  string | undefined,
  {
    state: RootState;
    extra: ThunkExtra;
    rejectValue: string;
  }
>;
